// Use centralized API client for proper authentication handling
import { locationsApi } from './apiClient';



/**
 * Service for handling location-related API interactions with caching
 */
class LocationService {
  /**
   * Cache storage
   * @private
   */
  static cache = {
    locations: null,
    timestamp: null,
    ttl: 5 * 60 * 1000, // 5 minutes cache TTL
  };

  /**
   * Check if cache is valid
   * @private
   * @returns {boolean} True if cache is valid
   */
  static isCacheValid() {
    if (!this.cache.locations || !this.cache.timestamp) {
      return false;
    }
    const now = Date.now();
    return (now - this.cache.timestamp) < this.cache.ttl;
  }

  /**
   * Invalidate location cache
   */
  static invalidateCache() {
    console.log('[LocationService] Cache invalidated');
    this.cache.locations = null;
    this.cache.timestamp = null;
  }

  /**
   * Get all locations with optional filtering and caching
   * @param {Object} options - Options object
   * @param {Object} options.filters - Query parameters for filtering locations
   * @param {boolean} options.useCache - Whether to use cache (default: true)
   * @param {boolean} options.forceRefresh - Force refresh even if cache is valid
   * @returns {Promise<Object>} Response object with locations data
   */
  static async getAllLocations({ filters = {}, useCache = true, forceRefresh = false } = {}) {
    try {
      // Return cached data if available and valid
      if (useCache && !forceRefresh && LocationService.isCacheValid()) {
        console.log('[LocationService] Returning cached locations');
        return LocationService.cache.locations;
      }

      console.log('[LocationService] Fetching locations from API');

      // Use centralized API client which handles authentication automatically
      const params = {
        limit: filters.limit || 5000,
        ...filters
      };
      const response = await locationsApi.getAll(params);

      console.log('[LocationService] Raw API response:', response);

      // Cache the entire response object to maintain structure
      LocationService.cache.locations = response;
      LocationService.cache.timestamp = Date.now();

      return response;
    } catch (error) {
      console.error('LocationService.getAllLocations error:', error);
      throw error;
    }
  }

  /**
   * Get location statistics (for dashboard)
   * @param {Object} options - Options object
   * @param {boolean} options.useCache - Whether to use cache (default: true)
   * @returns {Promise<Object>} Location statistics
   */
  static async getStatistics({ useCache = true } = {}) {
    try {
      // If we have cached locations, calculate stats from cache
      if (useCache && LocationService.isCacheValid()) {
        console.log('[LocationService] Calculating statistics from cache');
        const response = LocationService.cache.locations;
        const locations = response?.data?.locations ||
                         response?.data?.data ||
                         response?.data?.docs ||
                         response?.data ||
                         [];

        return {
          data: {
            total: Array.isArray(locations) ? locations.length : 0
          }
        };
      }

      // Otherwise fetch from API
      console.log('[LocationService] Fetching statistics from API');
      try {
        const response = await locationsApi.getStatistics();
        return response;
      } catch (apiError) {
        // If statistics endpoint doesn't exist, fetch all locations and count
        console.warn('[LocationService] Statistics endpoint not available, using getAllLocations');
        const response = await LocationService.getAllLocations({ useCache });
        const locations = response?.data?.locations ||
                         response?.data?.data ||
                         response?.data?.docs ||
                         response?.data ||
                         [];

        return {
          data: {
            total: Array.isArray(locations) ? locations.length : 0
          }
        };
      }
    } catch (error) {
      console.error('LocationService.getStatistics error:', error);
      return { data: { total: 0 } };
    }
  }

  /**
   * Get location by ID
   * @param {string} locationId - Location identifier
   * @returns {Promise<Object>} Location object
   */
  static async getLocationById(locationId) {
    try {
      const response = await locationsApi.getById(locationId);
      return response.data;
    } catch (error) {
      console.error('LocationService.getLocationById error:', error);
      throw error;
    }
  }

  /**
   * Create new location
   * @param {Object} locationData - Location data to create
   * @returns {Promise<Object>} Created location object
   */
  static async createLocation(locationData) {
    try {
      const response = await locationsApi.create(locationData);
      // Invalidate cache since locations changed
      LocationService.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('LocationService.createLocation error:', error);
      throw error;
    }
  }

  /**
   * Update existing location
   * @param {string} locationId - Location identifier
   * @param {Object} locationData - Updated location data
   * @returns {Promise<Object>} Updated location object
   */
  static async updateLocation(locationId, locationData) {
    try {
      const response = await locationsApi.update(locationId, locationData);
      // Invalidate cache since locations changed
      LocationService.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('LocationService.updateLocation error:', error);
      throw error;
    }
  }

  /**
   * Delete location
   * @param {string} locationId - Location identifier
   * @returns {Promise<void>}
   */
  static async deleteLocation(locationId) {
    try {
      await locationsApi.delete(locationId);
      // Invalidate cache since locations changed
      LocationService.invalidateCache();
    } catch (error) {
      console.error('LocationService.deleteLocation error:', error);
      throw error;
    }
  }

  /**
   * Get location hierarchy
   * @param {string} [rootId] - Optional root location ID
   * @returns {Promise<Array>} Hierarchical location tree
   */
  static async getLocationHierarchy(rootId = null) {
    try {
      const response = await locationsApi.getHierarchy();
      return response.data;
    } catch (error) {
      console.error('LocationService.getLocationHierarchy error:', error);
      throw error;
    }
  }

  /**
   * Fetch locations as a tree structure
   * @returns {Promise<Array>} Tree-structured location data
   */
  static async fetchLocationsTree() {
    try {
      // First try to get hierarchy endpoint if available
      try {
        const response = await locationsApi.getHierarchy();
        return response.data;
      } catch (hierarchyError) {
        // If hierarchy endpoint doesn't exist, build tree from flat list
        const response = await locationsApi.getAll();
        const locations = response.data.docs || response.data;

        // Build hierarchy from flat list
        const locationMap = new Map();
        const rootLocations = [];

        // First pass: create map of all locations
        locations.forEach(location => {
          locationMap.set(location._id || location.id, {
            ...location,
            id: location._id || location.id,
            children: [],
            deviceCount: location.deviceCount || 0
          });
        });

        // Second pass: build hierarchy
        locations.forEach(location => {
          const locationId = location._id || location.id;
          const parentId = location.parent || location.parentId;
          const currentLocation = locationMap.get(locationId);

          if (parentId && locationMap.has(parentId)) {
            const parent = locationMap.get(parentId);
            parent.children.push(currentLocation);
          } else {
            rootLocations.push(currentLocation);
          }
        });

        return rootLocations;
      }
    } catch (error) {
      console.error('LocationService.fetchLocationsTree error:', error);
      return [];
    }
  }

  /**
   * Get devices for a specific location
   * @param {string} locationId - Location identifier
   * @returns {Promise<Array>} Array of device objects
   */
  static async getLocationDevices(locationId) {
    try {
      const response = await locationsApi.getDevices(locationId);
      return response.data;
    } catch (error) {
      console.error('LocationService.getLocationDevices error:', error);
      throw error;
    }
  }

  /**
   * Update location permissions
   * @param {string} locationId - Location identifier
   * @param {Array} permissions - Array of permission objects
   * @returns {Promise<Object>} Updated permissions
   */
  static async updateLocationPermissions(locationId, permissions) {
    try {
      // Note: This endpoint might need to be added to locationsApi in apiClient.js
      // For now, we'll use the update method with permissions data
      const response = await locationsApi.update(locationId, { permissions });
      return response.data;
    } catch (error) {
      console.error('LocationService.updateLocationPermissions error:', error);
      throw error;
    }
  }

  /**
   * Get all locations as a simple flat array
   * Used for dropdowns and selection components
   * @returns {Promise<Array>} Array of location objects
   */
  static async getLocations() {
    try {
      const response = await LocationService.getAllLocations({ useCache: true });
      const locations = response?.data?.data ||
                       response?.data?.locations ||
                       response?.data?.docs ||
                       response?.data ||
                       [];
      return Array.isArray(locations) ? locations : [];
    } catch (error) {
      console.error('LocationService.getLocations error:', error);
      return [];
    }
  }

  /**
   * Get sites (top-level locations) accessible to the current user
   * Admins get all sites, non-admin users get sites from their LocationAccess
   * @returns {Promise<Object>} Response with sites array and meta information
   */
  static async getUserSites() {
    try {
      console.log('[LocationService] Fetching user accessible sites');
      const response = await locationsApi.getSites();
      console.log('[LocationService] User sites response:', response);
      return response;
    } catch (error) {
      console.error('LocationService.getUserSites error:', error);
      throw error;
    }
  }

  /**
   * Get all location IDs within a site (site + all children)
   * Useful for filtering queries by site
   * @param {number} siteId - Site location ID
   * @returns {Promise<Object>} Response with locationIds array
   */
  static async getSiteLocationIds(siteId) {
    try {
      console.log('[LocationService] Fetching location IDs for site:', siteId);
      const response = await locationsApi.getSiteLocationIds(siteId);
      return response;
    } catch (error) {
      console.error('LocationService.getSiteLocationIds error:', error);
      throw error;
    }
  }

}

export default LocationService;